import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../Modal';
import { FormErrorBanner } from '../FormErrorBanner';
import {
  hiringLinksAPI,
  type HiringLink,
  type HiringRaiseResult,
  type HiringRaiseTeam,
  type HiringRequest,
  type HiringRequestReview,
} from '../../services/api';
import { HiringRequestStanding, HiringRequestWaitingTag } from './HiringRequestCard';
import { RaiseRequestStatus } from './RaiseRequestStatus';
import { HiringTeamSection, useHiringTeam } from './HiringTeamSection';
import { KeyValues } from './HiringForm';
import { RaiseReview } from './RaiseReview';
import {
  DECLINE_REASON_MAX,
  DECLINE_REASON_MIN,
  RAISE_WORDS,
  REQUEST_CODES,
  REQUEST_FORM,
  REQUEST_WORDS,
  REVIEW_WORDS,
  TEAM_CODES,
  hiringCodeOf,
  hiringStatusOf,
  isHiringOff,
  isRaiseOff,
  isTeamRefusal,
  listIsBehindAfter,
  nothingWasSentBy,
  localiseRaiseMessage,
  makeRaiseRequestKey,
  namedCode,
  raiseErrorText,
  raiseLinkMoved,
  raiseOutcomeHeading,
  raiseOutcomeKind,
  raiseRefusalStep,
  requestOpeningFor,
  requestReasonLabel,
  requestTicksLine,
  reviewReplacesWords,
  standingAfterRefusal,
  teamMissingLine,
  teamNotAllowedOf,
} from './hiringText';
import type { HiringRequestsCan } from './useHiringRequests';

/**
 * One hiring request: what was asked, where it stands, and what this person may do
 * about it.
 *
 * A hiring request has sent NOTHING to Recruitment. A person who may raise hiring for the
 * project confirms it here, and only that confirm sends: the server takes the form the
 * request was saved with, checks everything again in the confirmer's own 1Office sign-in,
 * and raises it in their name. So this window follows the Raise hiring window's rules for
 * the one click that can create an opening:
 *
 *   - Opening the window sends nothing. For a person who may confirm, it shows what was
 *     asked, read-only, and below it the Recruitment team section: the person who asked
 *     picks no team, so the confirmer does, opening with the budget's defaults as they are
 *     now (product owner, 14 Oct 2026). "Review" then asks for a FRESH review, in their own
 *     1Office sign-in, with that team: a read, like the Raise window's "Review".
 *   - The request key is made ONCE per review and goes with every repeat of its confirm.
 *     The server answers a repeat from the first one, so one review can never create two
 *     openings. A new review ("Review" again, "Review again") gets a new key. The confirm
 *     carries the team that was reviewed; another team would be refused as stale.
 *   - "Yes, raise hiring" is disabled from the first click until an answer, or a failure,
 *     is on screen.
 *   - When no answer comes back, the only thing to press repeats the SAME confirm with the
 *     same key, and neither Decline nor Withdraw is offered: the request may have been sent.
 *   - A create Recruitment did not confirm is shown as the Raise window shows it, and
 *     never offers "Raise again". When it is later closed with nothing created, the
 *     request waits again: the window says so once the owner's re-read list has it, and
 *     offers "Review again" (a new review, a new key).
 *
 * WHO MAY CONFIRM OR DECLINE: a person with "Raise & cancel hiring" (a row of its own in
 * Access Management, since 14 Oct 2026). The list of hiring requests says so (`can`), and
 * the fresh review, the confirm and the decline are offered only then; everyone else sees
 * the request and "Close" (and their own request's "Withdraw"). 1Office Project-Management's
 * own who-may-create rule is not checked any more.
 *
 * Nothing the person asked can be changed here: a hiring request is confirmed as it was
 * asked, with the team the confirmer picks, or declined with a reason. Declining, and
 * withdrawing one's own, take two clicks and send nothing to Recruitment. A person who can
 * do neither sees the request and "Close".
 *
 * Every sentence about where the request stands, and every refusal, is the server's own.
 * The person who asked is shown by employee code; recruitment staff by name and code (read
 * fresh, never kept). No phone number is drawn.
 */

const PRIMARY = 'inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-primary-600 px-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50';
const SECONDARY = 'inline-flex h-9 items-center rounded-lg border border-gray-300 bg-white px-3.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50';
const DANGER_QUIET = 'inline-flex h-9 items-center rounded-lg px-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50';

/** What the person asked, read-only: the choices of their form as label-and-value lines. */
function AskedFacts({ request }: { request: HiringRequest }) {
  const ticks = requestTicksLine(request.ticks);
  return (
    <section aria-label={REQUEST_WORDS.factsLabel} className="space-y-2">
      <div className="flex justify-end empty:hidden"><HiringRequestWaitingTag request={request} /></div>
      <KeyValues
        rows={[
          { key: 'by', label: REQUEST_FORM.askedBy, value: REQUEST_FORM.askedOn(request) },
          { key: 'for', label: REQUEST_FORM.openingFor, value: requestOpeningFor(request) },
          { key: 'why', label: REQUEST_FORM.why, value: requestReasonLabel(request.reason) },
          { key: 'replaces', label: REQUEST_FORM.replaces, value: request.replacesEmpCode ? namedCode(request.replacesEmpCode, request.replacesName) : null },
          { key: 'budget', label: REQUEST_FORM.budget, value: request.budget?.name ?? null },
          { key: 'ticked', label: REQUEST_FORM.ticked, value: ticks ? ticks.replace(/^Ticked by the person who asked: /, '') : null },
          { key: 'note', label: REQUEST_FORM.note, value: request.note ? `“${request.note}”` : null },
        ]}
      />
    </section>
  );
}
const SMALL_KEEP = 'rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50';
const SMALL_DANGER = 'inline-flex items-center gap-1 rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50';

interface Reviewed {
  preview: HiringRequestReview;
  /** Made when this review was answered; every repeat of its confirm carries it. */
  requestKey: string;
  /** The team this review was made with. The confirm sends this same team. */
  team: HiringRaiseTeam;
}

export function HiringRequestWindow({
  projectId, request, can, start, notice, onClose, onRequest, onLink, onOutOfDate, onHiringGone, onRaiseGone,
}: {
  projectId: number;
  /**
   * One short line at the top of the window, from whoever opened it for one step (the question
   * asked when someone is deployed: "<person> is deployed. Now finish withdrawing the hiring request.").
   */
  notice?: string;
  /** The request as the owner has it now. What the server answers in this window is shown in its place. */
  request: HiringRequest;
  /** What the list of hiring requests said this person may do. */
  can: HiringRequestsCan;
  /**
   * "Decline" or "Withdraw" was already chosen outside this window (the question asked when
   * someone is deployed, DeployHiringCheck, opens it once the deploy is saved): the window opens
   * on that question, where it would offer it.
   * The second click, and everything after it, is the same.
   */
  start?: 'decline' | 'withdraw';
  onClose: () => void;
  /** The request as the server just answered it: confirmed, waiting again, declined or withdrawn. */
  onRequest: (request: HiringRequest) => void;
  /** A confirm made a link, or a check moved it on: the link as the server just answered it. */
  onLink: (link: HiringLink) => void;
  /** The owner's lists may be behind: an answer was lost, or the request was decided somewhere else. */
  onOutOfDate?: () => void;
  /** Hiring was switched off for the project while the window was open. */
  onHiringGone: () => void;
  /** Raising was switched off, for the project or on the server, while the window was open. */
  onRaiseGone: () => void;
}) {
  // The request as the server last answered it in this window.
  const [answered, setAnswered] = useState<HiringRequest | null>(null);
  const current = answered ?? request;

  // ── the fresh review ──
  const [checking, setChecking] = useState(false);
  const [reviewed, setReviewed] = useState<Reviewed | null>(null);
  // The review was refused: the server's sentence, and whether a form would have answered it.
  const [refusal, setRefusal] = useState<{ sentence: string; needsChange: boolean } | null>(null);

  // ── the confirm ──
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  // No answer came back: only the same confirm may be repeated.
  const [lost, setLost] = useState(false);
  // An answer was lost at some point in this review: from then on only an outcome ends it.
  const [locked, setLocked] = useState(false);
  // The server said it could not check just now, and that nothing was sent: its sentence
  // stands alone, and the button repeats the same confirm ("Ask again").
  const [couldNotCheck, setCouldNotCheck] = useState(false);
  const [result, setResult] = useState<HiringRaiseResult | null>(null);
  // The request as the confirm answered it. Not known any more once a later check moved the create on.
  const [resultRequest, setResultRequest] = useState<HiringRequest | null>(null);

  // ── declining, or withdrawing one's own ──
  // Opened on a question (`start`) only where the window would offer it itself; otherwise as always.
  const [asking, setAsking] = useState<'decline' | 'withdraw' | null>(() => {
    if (request.standing !== 'waiting') return null;
    if (start === 'withdraw' && request.mine) return 'withdraw';
    if (start === 'decline' && can.decline && !request.mine) return 'decline';
    return null;
  });
  const [reason, setReason] = useState('');
  const [ending, setEnding] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  // A refusal said the request is no longer waiting, or no longer there: its sentence.
  const [gone, setGone] = useState<string | null>(null);

  const alive = useRef(true);
  // One confirm at a time, however fast the clicks come.
  const sending = useRef(false);
  const owner = useRef({ onRequest, onLink, onOutOfDate, onHiringGone, onRaiseGone });
  owner.current = { onRequest, onLink, onOutOfDate, onHiringGone, onRaiseGone };
  const standingNow = useRef(current.standing);
  standingNow.current = current.standing;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /** A refusal that ends the window: hiring, or raising, was switched off. */
  const switchedOff = (err: unknown): boolean => {
    if (isHiringOff(err)) {
      owner.current.onHiringGone();
      return true;
    }
    if (isRaiseOff(err)) {
      owner.current.onRaiseGone();
      return true;
    }
    return false;
  };

  /**
   * A refusal that says the request was decided a moment ago (confirmed, declined,
   * withdrawn) or is not there: its sentence is shown, nothing more is offered, and the
   * owner's lists are read again.
   */
  const decidedElsewhere = (err: unknown): boolean => {
    const code = hiringCodeOf(err);
    if (code !== REQUEST_CODES.notWaiting && code !== REQUEST_CODES.notFound) return false;
    owner.current.onOutOfDate?.();
    if (alive.current) {
      // The request is no longer as it was opened. Its standing is the one the refusal
      // names, and what belongs only to a waiting request - how long it has waited, what
      // stands in its way - is not drawn beside the sentence that says it was decided.
      const standing = standingAfterRefusal(err);
      setAnswered((was) => {
        const shown = was ?? request;
        return { ...shown, standing: standing && standing !== 'waiting' ? standing : shown.standing, old: false, waitingDays: null, notes: [] };
      });
      setGone(raiseErrorText(err, REQUEST_WORDS.noLongerWaiting));
      setReviewed(null);
      setRefusal(null);
      setConfirmError(null);
      setLost(false);
      setLocked(false);
      setAsking(null);
    }
    return true;
  };
  const refusedHere = useRef({ switchedOff, decidedElsewhere });
  refusedHere.current = { switchedOff, decidedElsewhere };

  // The Recruitment team the person who confirms picks, read for the request's budget while
  // the request waits and they may confirm it.
  const mayConfirm = can.confirm;
  const teamOpen = mayConfirm && current.standing === 'waiting' && !gone;
  const team = useHiringTeam({
    projectId,
    budgetId: teamOpen && typeof current.budget?.id === 'number' ? current.budget.id : null,
    enabled: teamOpen,
    onRefused: (err) => refusedHere.current.switchedOff(err),
  });
  const teamRef = useRef(team);
  teamRef.current = team;

  /**
   * A refusal answered in the team section: the people it names are marked, or the
   * budget's rounds are read, and filled, again.
   */
  const refusedForTeam = (err: unknown) => {
    const code = hiringCodeOf(err);
    if (code === TEAM_CODES.personNotAllowed) teamRef.current.markNotAllowed(teamNotAllowedOf(err));
    if (code === TEAM_CODES.roundsChanged) teamRef.current.reload();
  };

  /**
   * The fresh review, in this person's own 1Office sign-in, with the team they picked. It
   * reads; it sends nothing. A refusal is shown with the server's sentence; "Decline it, or
   * raise it yourself" is added only for a refusal a form would have answered, and not for
   * one about the team, which is answered right here.
   */
  const review = async () => {
    const picked = team.team;
    if (!team.ready || !picked || checking) return;
    setChecking(true);
    setReviewed(null);
    setRefusal(null);
    setConfirmError(null);
    setLost(false);
    setLocked(false);
    try {
      const res = await hiringLinksAPI.reviewRequest(projectId, request.id, picked);
      if (!alive.current) return;
      const preview = res?.data?.data;
      // A review with nothing to read is not a review: nothing may be confirmed on it.
      if (!preview || typeof preview.previewHash !== 'string' || !Array.isArray(preview.sentences) || preview.sentences.length === 0) {
        setRefusal({ sentence: REQUEST_WORDS.reviewUnreadable, needsChange: false });
        return;
      }
      if (preview.request) setAnswered(preview.request);
      // A new review, so a new key: this one belongs to this review alone.
      setReviewed({ preview, requestKey: makeRaiseRequestKey(), team: picked });
      setCouldNotCheck(false);
    } catch (err) {
      if (refusedHere.current.switchedOff(err) || refusedHere.current.decidedElsewhere(err)) return;
      // Something the page does not show yet is in the way: its lists are read again.
      if (listIsBehindAfter(err)) owner.current.onOutOfDate?.();
      if (!alive.current) return;
      refusedForTeam(err);
      // One that says to wait (hiring is being raised for it), or that the budget or
      // Recruitment is in the way, would refuse a raise made by hand too: its own sentence
      // stands alone.
      const step = raiseRefusalStep(err);
      setRefusal({ sentence: raiseErrorText(err, REQUEST_WORDS.reviewFailed), needsChange: step === 'form' && !isTeamRefusal(err) });
    } finally {
      if (alive.current) setChecking(false);
    }
  };

  const confirm = async () => {
    if (!reviewed || sending.current) return;
    sending.current = true;
    setConfirming(true);
    setConfirmError(null);
    try {
      const res = await hiringLinksAPI.confirmRequest(projectId, request.id, {
        requestKey: reviewed.requestKey,
        previewHash: reviewed.preview.previewHash,
        team: reviewed.team,
      });
      const got = res?.data?.data;
      if (!got || !got.link) {
        // An answer that cannot be read is no answer: only the same confirm may be repeated.
        owner.current.onOutOfDate?.();
        if (alive.current) {
          setLost(true);
          setLocked(true);
        }
        return;
      }
      // The owner is told whether or not this window is still open.
      owner.current.onLink(got.link);
      if (got.request) owner.current.onRequest(got.request);
      else owner.current.onOutOfDate?.();
      if (!alive.current) return;
      if (got.request) setAnswered(got.request);
      setLost(false);
      setResultRequest(got.request ?? null);
      setResult(got);
    } catch (err) {
      if (switchedOff(err)) return;
      const status = hiringStatusOf(err);
      const refused = status !== undefined && status < 500 && !!hiringCodeOf(err);
      if (!refused && nothingWasSentBy(err)) {
        // A server error that says by its code that NOTHING WAS SENT: Recruitment did not
        // answer a check, which is made before anything is claimed. The server's sentence
        // stands alone - never beside "ChannelPlex does not know whether this reached
        // Recruitment" - and the only thing to press repeats this same confirm, with this same key.
        if (!alive.current) return;
        setLost(false);
        setCouldNotCheck(true);
        setConfirmError(raiseErrorText(err, RAISE_WORDS.refusedPlain));
        return;
      }
      if (!refused) {
        // No answer, or an error of the server's own: the confirm may have gone through.
        owner.current.onOutOfDate?.();
        if (!alive.current) return;
        setLost(true);
        setLocked(true);
        setConfirmError(hiringCodeOf(err) ? raiseErrorText(err, '') || null : null);
        return;
      }
      // A definite "no": nothing was sent by this confirm.
      if (decidedElsewhere(err)) return;
      if (listIsBehindAfter(err)) owner.current.onOutOfDate?.();
      if (!alive.current) return;
      setLost(false);
      setCouldNotCheck(false);
      const sentence = raiseErrorText(err, RAISE_WORDS.refusedPlain);
      const next = raiseRefusalStep(err);
      if (!locked && (next === 'review_again' || next === 'form')) {
        // What was reviewed is not what would be sent. The review and its key are gone;
        // "Review" makes a new one of each, with the team as it is then.
        refusedForTeam(err);
        setReviewed(null);
        setRefusal({ sentence, needsChange: next === 'form' && !isTeamRefusal(err) });
      } else {
        // The sentence is shown, and the only thing to press repeats this same confirm with this same key.
        setConfirmError(sentence);
      }
    } finally {
      sending.current = false;
      if (alive.current) setConfirming(false);
    }
  };

  /** Declined, or withdrawn: recorded in ChannelPlex. Nothing is sent to Recruitment. */
  const end = async (what: 'decline' | 'withdraw') => {
    if (ending) return;
    setEnding(true);
    setEndError(null);
    const failed = what === 'decline' ? REQUEST_WORDS.declineFailed : REQUEST_WORDS.withdrawFailed;
    try {
      const res = what === 'decline'
        ? await hiringLinksAPI.declineRequest(projectId, request.id, reason.trim())
        : await hiringLinksAPI.withdrawRequest(projectId, request.id);
      const got = res?.data?.data?.request;
      if (!got) {
        // An answer that cannot be read: the lists are read again, and the question stays.
        owner.current.onOutOfDate?.();
        if (alive.current) setEndError(failed);
        return;
      }
      // The owner is told whether or not this window is still open.
      owner.current.onRequest(got);
      if (!alive.current) return;
      setAnswered(got);
      setReviewed(null);
      setRefusal(null);
      setAsking(null);
      setSaid(what === 'decline' ? REQUEST_WORDS.declined : REQUEST_WORDS.withdrawn);
    } catch (err) {
      if (switchedOff(err) || decidedElsewhere(err)) return;
      if (alive.current) setEndError(raiseErrorText(err, failed));
    } finally {
      if (alive.current) setEnding(false);
    }
  };

  const close = () => {
    // A confirm that is out, or whose answer was lost, may be on the owner's lists by now.
    if (confirming || locked) onOutOfDate?.();
    onClose();
  };

  /** A check, or a person's choice, was answered for the create this confirm sent. */
  const took = (next: HiringRaiseResult) => {
    const moved = !result || raiseLinkMoved(result.link, next.link);
    if (moved) {
      owner.current.onLink(next.link);
      // Where the hiring request stands follows the create: the owner reads its lists again.
      owner.current.onOutOfDate?.();
    }
    if (!alive.current) return;
    if (moved) setResultRequest(null);
    setResult(next);
  };

  /**
   * A request that waits again: back to what was asked and the team section, for a new
   * review under a new key. It starts from the request as it stands now.
   */
  const reviewAgain = (waitingAgain: HiringRequest) => {
    setAnswered(waitingAgain);
    setResult(null);
    setResultRequest(null);
    setReviewed(null);
    setRefusal(null);
    setConfirmError(null);
    setLost(false);
    setLocked(false);
    setCouldNotCheck(false);
  };

  const title = REQUEST_FORM.title;
  const closeButton = <button type="button" className={SECONDARY} onClick={close}>{REQUEST_WORDS.close}</button>;

  // ── the result of a confirm ──
  if (result) {
    const outcome = raiseOutcomeKind(result.outcome);
    const tone = outcome === 'created'
      ? (result.link.attention?.includes('created_incomplete') || result.link.attention?.includes('not_listed')
        ? 'border-amber-200 bg-amber-50 text-amber-900'
        : 'border-green-200 bg-green-50 text-green-900')
      : 'border-gray-200 bg-gray-50 text-gray-800';
    // Where the hiring request stands once nothing was created. The confirm's own answer
    // says it (resultRequest). A LATER check or close of that create ("Nothing was created,
    // close this request") does not: the owner then reads its lists again, and the request
    // as a list has it (the `request` prop) is this one after this very confirm when its
    // last confirm claimed the link on show. Until that list has arrived nothing is said.
    const reread = !resultRequest && request.confirmed?.linkId === result.link.id
      && (request.standing === 'waiting' || request.standing === 'not_created') ? request : null;
    const standsNow = outcome === 'nothing' ? resultRequest ?? reread : null;
    // It waits again: it can be reviewed afresh, under a new key.
    const waitsAgain = standsNow?.standing === 'waiting' ? standsNow : null;
    return (
      <Modal
        isOpen
        onClose={close}
        title={title}
        size="lg"
        footer={(
          <>
            {closeButton}
            {waitsAgain && can.confirm && (
              <button type="button" className={PRIMARY} onClick={() => reviewAgain(waitsAgain)}>{REQUEST_WORDS.reviewAgain}</button>
            )}
          </>
        )}
      >
        <div className="space-y-3">
          <AskedFacts request={standsNow ?? current} />
          <h4 className="border-t border-gray-100 pt-3 text-sm font-semibold text-gray-900">{raiseOutcomeHeading(result.outcome)}</h4>
          {outcome === 'waiting' ? (
            <RaiseRequestStatus
              projectId={projectId}
              link={result.link}
              canManage
              initial={result}
              onResult={took}
              onHiringGone={onHiringGone}
            />
          ) : (
            <p role="status" className={`rounded-lg border px-3 py-2 text-sm ${tone}`}>{localiseRaiseMessage(result.message)}</p>
          )}
          {/* Where the hiring request stands now: waiting again, or not created. */}
          {standsNow && <HiringRequestStanding request={standsNow} />}
        </div>
      </Modal>
    );
  }

  const waiting = current.standing === 'waiting' && !gone;
  // While a confirm is out, or its answer was lost, the request may already have been sent.
  const settled = !confirming && !lost && !locked;
  const offerConfirm = waiting && can.confirm;
  // One's own request is withdrawn; another person's is declined, with a reason they will read.
  const offerWithdraw = waiting && current.mine && settled;
  const offerDecline = waiting && can.decline && !current.mine && settled;
  const reasonText = reason.trim();
  const reasonOk = reasonText.length >= DECLINE_REASON_MIN && reasonText.length <= DECLINE_REASON_MAX;
  const teamLine = offerConfirm && !reviewed && settled ? teamMissingLine(team.draft) : null;
  const footLine = offerConfirm && reviewed ? REQUEST_WORDS.confirmFootnote(namedCode(current.requestedBy, current.requestedByName)) : teamLine;

  return (
    <Modal
      isOpen
      onClose={close}
      title={title}
      size="lg"
      footer={(
        <div className="flex w-full flex-col gap-2">
          {offerConfirm && lost && (
            <div role="alert" className="space-y-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {confirmError && <p>{confirmError}</p>}
              <p className="font-medium">{RAISE_WORDS.lostAnswer}</p>
              <p>{RAISE_WORDS.lostAnswerNext}</p>
            </div>
          )}
          {offerConfirm && !lost && confirmError && <FormErrorBanner message={confirmError} />}
          {offerConfirm && !lost && locked && <p className="text-xs text-amber-900">{RAISE_WORDS.lockedNote}</p>}
          {confirming && <p className="text-right text-xs text-gray-600">{RAISE_WORDS.sendingNote}</p>}
          {/* On a phone the note has the full width above the buttons, which keep to one row
              (the window's own × closes it there when the review's two buttons are drawn). */}
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            {footLine && <span className="w-full text-[12.5px] text-gray-500 sm:w-auto sm:min-w-0 sm:flex-1">{footLine}</span>}
            {!asking && offerWithdraw && (
              <button type="button" className={DANGER_QUIET} onClick={() => { setEndError(null); setAsking('withdraw'); }}>
                {REQUEST_WORDS.withdraw}
              </button>
            )}
            {!asking && offerDecline && (
              <button type="button" className={DANGER_QUIET} onClick={() => { setEndError(null); setAsking('decline'); }}>
                {REQUEST_WORDS.decline}
              </button>
            )}
            {offerConfirm && reviewed && !asking && !lost && !locked && !couldNotCheck
              ? <button type="button" className={`${SECONDARY} max-sm:hidden`} onClick={close}>{REQUEST_WORDS.close}</button>
              : closeButton}
            {/* What was asked, and the team: "Review" asks for the fresh review with it. */}
            {!asking && offerConfirm && !reviewed && (
              <button type="button" className={PRIMARY} onClick={() => void review()} disabled={!team.ready || checking}>
                {checking && <Loader2 className="h-4 w-4 animate-spin" />}
                {checking ? RAISE_WORDS.reviewing : RAISE_WORDS.review}
              </button>
            )}
            {!asking && offerConfirm && reviewed && (lost || locked || couldNotCheck ? (
              // The same confirm, with the same key. Never a new one.
              <button type="button" className={PRIMARY} onClick={() => void confirm()} disabled={confirming}>
                {confirming && <Loader2 className="h-4 w-4 animate-spin" />}
                {confirming ? RAISE_WORDS.sending : RAISE_WORDS.askAgain}
              </button>
            ) : (
              <>
                <button type="button" className={SECONDARY} onClick={() => setReviewed(null)} disabled={confirming}>
                  {REVIEW_WORDS.back}
                </button>
                <button type="button" className={PRIMARY} onClick={() => void confirm()} disabled={confirming}>
                  {confirming && <Loader2 className="h-4 w-4 animate-spin" />}
                  {confirming ? RAISE_WORDS.sending : reviewed.preview.confirm?.yes || REQUEST_WORDS.yes}
                </button>
              </>
            ))}
          </div>
        </div>
      )}
    >
      <div className="space-y-4">
        {notice && <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">{notice}</p>}
        {!reviewed && <AskedFacts request={current} />}

        {gone ? (
          <p role="status" className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">{gone}</p>
        ) : (
          !reviewed && <HiringRequestStanding request={current} />
        )}
        {said && (
          <p role="status" className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">{said}</p>
        )}

        {offerConfirm && !checking && refusal && (
          <div className="space-y-2">
            <FormErrorBanner message={refusal.sentence} />
            {refusal.needsChange && <p className="text-sm text-gray-700">{REQUEST_WORDS.cannotChange(current.subject.kind)}</p>}
          </div>
        )}
        {/* The Recruitment team the person who confirms picks, until it is reviewed. */}
        {offerConfirm && !reviewed && settled && <HiringTeamSection team={team} busy={checking} />}
        {offerConfirm && reviewed && (
          <RaiseReview
            preview={reviewed.preview}
            asking={false}
            facts={{
              openingFor: requestOpeningFor(current),
              replaces: current.replacesEmpCode
                ? reviewReplacesWords({ empCode: current.replacesEmpCode, name: reviewed.preview.replacement?.name ?? null })
                : null,
              budgetName: current.budget?.name ?? '',
              budgetFree: null,
              address: null,
              subjectKind: current.subject.kind,
            }}
          />
        )}

        {/* Two clicks: the first only asks. Neither sends anything to Recruitment. */}
        {asking === 'decline' && offerDecline && (
          <div role="group" aria-label={REQUEST_WORDS.declineGroupLabel} className="space-y-2 rounded-lg border border-red-100 bg-red-50/40 p-3">
            <label className="block text-sm font-medium text-gray-800" htmlFor="hiring-request-decline-reason">
              {REQUEST_WORDS.declineQuestion}
            </label>
            <textarea
              id="hiring-request-decline-reason"
              rows={2}
              autoFocus
              value={reason}
              maxLength={DECLINE_REASON_MAX}
              onChange={(e) => setReason(e.target.value)}
              disabled={ending}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
            <p className="text-xs text-gray-500">{REQUEST_WORDS.declineHint}</p>
            {endError && <FormErrorBanner message={endError} />}
            <div className="flex items-center justify-end gap-2">
              <button type="button" className={SMALL_KEEP} onClick={() => setAsking(null)} disabled={ending}>
                {REQUEST_WORDS.declineNo}
              </button>
              <button type="button" className={SMALL_DANGER} onClick={() => void end('decline')} disabled={ending || !reasonOk}>
                {ending && <Loader2 className="h-3 w-3 animate-spin" />}
                {REQUEST_WORDS.declineYes}
              </button>
            </div>
          </div>
        )}
        {asking === 'withdraw' && offerWithdraw && (
          <div role="group" aria-label={REQUEST_WORDS.withdrawGroupLabel} className="space-y-2 rounded-lg border border-red-100 bg-red-50/40 p-3">
            <p className="text-sm font-medium text-gray-800">{REQUEST_WORDS.withdrawQuestion}</p>
            {endError && <FormErrorBanner message={endError} />}
            <div className="flex items-center justify-end gap-2">
              {/* Focus lands on the safe answer. */}
              <button type="button" autoFocus className={SMALL_KEEP} onClick={() => setAsking(null)} disabled={ending}>
                {REQUEST_WORDS.withdrawNo}
              </button>
              <button type="button" className={SMALL_DANGER} onClick={() => void end('withdraw')} disabled={ending}>
                {ending && <Loader2 className="h-3 w-3 animate-spin" />}
                {REQUEST_WORDS.withdrawYes}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default HiringRequestWindow;
